/**
 * API Client Service
 * Handles all backend API communication with authentication
 */

import axios, { AxiosInstance, AxiosError } from 'axios'
import type {
  User,
  UserSettings,
  Position,
  Balance,
  Instrument,
  Price,
  Candle,
  Timeframe,
  ChatMessage,
  ApprovalRequest,
  WatchlistItem,
  OrderRequest,
  OrderResult,
  ApiResponse,
} from '../types'

class ApiClient {
  private client: AxiosInstance
  private sessionId: string | null = null
  private requestCache: Map<string, { data: any; timestamp: number }> = new Map()
  private pendingRequests: Map<string, Promise<any>> = new Map()
  private readonly CACHE_TTL = 30000 // 30 seconds cache

  constructor() {
    const apiUrl = import.meta.env.VITE_API_URL || '/api'
    
    // Log API URL in development
    if (import.meta.env.DEV) {
      console.log('API Client initialized with baseURL:', apiUrl)
    }
    
    this.client = axios.create({
      baseURL: apiUrl,
      timeout: 45000, // Increased timeout for LLM responses
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor - add session ID
    this.client.interceptors.request.use(
      (config) => {
        // Only log non-health requests in development
        if (config.url !== '/health' && import.meta.env.DEV) {
          console.log('API Request:', config.method?.toUpperCase(), config.url)
        }
        if (this.sessionId) {
          config.headers['X-Session-ID'] = this.sessionId
        }
        return config
      },
      (error) => {
        console.error('Request interceptor error:', error)
        return Promise.reject(error)
      }
    )

    // Response interceptor - handle errors
    this.client.interceptors.response.use(
      (response) => {
        // Only log non-health requests in development
        if (response.config.url !== '/health' && import.meta.env.DEV) {
          console.log('API Response:', response.status, response.config.url)
        }
        return response
      },
      async (error: AxiosError<ApiResponse>) => {
        if (error.config?.url !== '/health' && import.meta.env.DEV) {
          console.error('API Error:', error.response?.status, error.response?.data, error.config?.url)
        }
        if (error.response?.status === 401) {
          // Only a SESSION failure from our own auth middleware should log the user out.
          // Upstream/broker failures are returned as 502 by the backend, but we also
          // guard on the error code so a stray 401 can never kick the user out.
          const code = (error.response?.data as any)?.error?.code
          const isSessionError = !code || code === 'SESSION_EXPIRED' || code === 'AUTHENTICATION_ERROR'
          const isLoginPage = window.location.pathname === '/login'
          const isAuthRequest = error.config?.url?.includes('/auth/google') || error.config?.url?.includes('/auth/login')

          if (isSessionError && !isLoginPage && !isAuthRequest) {
            // Prevent multiple redirects when several requests fail at once
            const lastRedirect = sessionStorage.getItem('lastAuthRedirect')
            const now = Date.now()

            if (!lastRedirect || now - parseInt(lastRedirect) > 3000) {
              console.warn('[API] Session invalid on server, logging out:', error.config?.url)
              sessionStorage.setItem('lastAuthRedirect', now.toString())
              this.clearSession()
              // Wipe persisted auth state too, otherwise the store rehydrates as
              // "authenticated" with no sessionId and bounces between pages.
              localStorage.removeItem('auth-storage')
              window.location.replace('/login')
            }
          }
        }
        return Promise.reject(error)
      }
    )
  }

  /**
   * Set session ID for authenticated requests
   */
  setSession(sessionId: string) {
    this.sessionId = sessionId
    localStorage.setItem('sessionId', sessionId)
  }

  /**
   * Clear session
   */
  clearSession() {
    this.sessionId = null
    localStorage.removeItem('sessionId')
    this.requestCache.clear()
  }

  /**
   * Load session from localStorage
   */
  loadSession() {
    const sessionId = localStorage.getItem('sessionId')
    if (sessionId) {
      this.sessionId = sessionId
    }
  }

  /**
   * Test API connection
   */
  async testConnection(): Promise<boolean> {
    try {
      const response = await this.client.get('/health')
      return response.status === 200
    } catch (error) {
      console.error('Health check failed:', error)
      return false
    }
  }

  /**
   * Test chat functionality
   */
  async testChat(): Promise<boolean> {
    try {
      const response = await this.client.get('/chat/status')
      if (import.meta.env.DEV) {
        console.log('Chat status response:', response.data)
      }
      return response.status === 200 && response.data.success
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Chat test failed:', error)
      }
      return false
    }
  }
  /**
   * Generic GET request with caching and deduplication
   */
  async get<T = any>(url: string, config?: any) {
    const cacheKey = `GET:${url}`
    
    // Check cache first (skip if noCache is true or if it's a polling endpoint)
    const isCacheable = !config?.noCache && !url.includes('/whatsapp/status') && !url.includes('/whatsapp/qr');
    const cached = this.requestCache.get(cacheKey)
    if (isCacheable && cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return Promise.resolve(cached.data)
    }
    
    // Check if request is already pending (deduplication)
    const pending = this.pendingRequests.get(cacheKey)
    if (isCacheable && pending) {
      return pending
    }
    
    // Make new request
    const request = this.client.get<T>(url, config).then(response => {
      // Cache the response
      this.requestCache.set(cacheKey, {
        data: response,
        timestamp: Date.now()
      })
      // Remove from pending
      this.pendingRequests.delete(cacheKey)
      return response
    }).catch(error => {
      // Remove from pending on error
      this.pendingRequests.delete(cacheKey)
      throw error
    })
    
    // Store as pending
    this.pendingRequests.set(cacheKey, request)
    return request
  }

  /**
   * Clear cache for specific URL or all
   */
  clearCache(url?: string) {
    if (url) {
      this.requestCache.delete(`GET:${url}`)
    } else {
      this.requestCache.clear()
    }
  }

  /**
   * Generic POST request
   */
  post<T = any>(url: string, data?: any, config?: any) {
    // Clear cache on POST (data mutation)
    this.clearCache()
    return this.client.post<T>(url, data, config)
  }

  /**
   * Generic PUT request
   */
  put<T = any>(url: string, data?: any, config?: any) {
    this.clearCache()
    return this.client.put<T>(url, data, config)
  }

  /**
   * Generic DELETE request
   */
  delete<T = any>(url: string, config?: any) {
    // Clear cache on DELETE (data mutation)
    this.clearCache()
    return this.client.delete<T>(url, config)
  }

  // ============================================
  // Authentication
  // ============================================

  async login(email: string, password: string): Promise<User> {
    const response = await this.client.post<ApiResponse<{ user: User; sessionId: string }>>(
      '/auth/login',
      { email, password }
    )
    if (response.data.data) {
      this.setSession(response.data.data.sessionId)
      return response.data.data.user
    }
    throw new Error('Login failed')
  }

  async logout(): Promise<void> {
    await this.client.post('/auth/logout')
    this.clearSession()
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<ApiResponse<{ user: User; settings: UserSettings }>>('/auth/me')
    if (response.data.data) {
      return response.data.data.user
    }
    throw new Error('Failed to get current user')
  }

  // ============================================
  // User Settings - Simplified
  // ============================================

  async getUserSettings(): Promise<UserSettings> {
    const response = await this.client.get<ApiResponse<UserSettings>>('/user/settings')
    if (response.data.data) {
      return response.data.data
    }
    throw new Error('Failed to get user settings')
  }

  async updatePreferences(preferences: Partial<UserSettings>): Promise<UserSettings> {
    const response = await this.client.put<ApiResponse<UserSettings>>(
      '/user/preferences',
      preferences
    )
    if (response.data.data) {
      return response.data.data
    }
    throw new Error('Failed to update preferences')
  }

  // ============================================
  // Trading
  // ============================================

  async executeMarketOrder(order: OrderRequest): Promise<OrderResult> {
    const response = await this.client.post<ApiResponse<OrderResult>>('/trades/market', order)
    if (response.data.data) {
      return response.data.data
    }
    throw new Error('Failed to execute market order')
  }

  async executeLimitOrder(order: OrderRequest): Promise<OrderResult> {
    const response = await this.client.post<ApiResponse<OrderResult>>('/trades/limit', order)
    if (response.data.data) {
      return response.data.data
    }
    throw new Error('Failed to execute limit order')
  }

  async closePosition(dealId: string): Promise<OrderResult> {
    // Skip AI analysis for faster position closing
    const response = await this.client.delete<ApiResponse<OrderResult>>(
      `/trades/${dealId}?skipAIAnalysis=true`
    )
    if (response.data.data) {
      return response.data.data
    }
    throw new Error('Failed to close position')
  }

  async validateOrder(order: OrderRequest): Promise<{
    valid: boolean
    error?: string
    fees: number
    availableBalance: number
  }> {
    const response = await this.client.post('/trades/validate', order)
    if (response.data.data) {
      return response.data.data
    }
    throw new Error('Failed to validate order')
  }

  // ============================================
  // Positions
  // ============================================

  async getOpenPositions(): Promise<Position[]> {
    const response = await this.client.get<ApiResponse<Position[]>>('/positions')
    return response.data.data || []
  }

  async getPositionById(id: string): Promise<Position> {
    const response = await this.client.get<ApiResponse<Position>>(`/positions/${id}`)
    if (response.data.data) {
      return response.data.data
    }
    throw new Error('Position not found')
  }

  async getPositionHistory(): Promise<Position[]> {
    const response = await this.client.get<ApiResponse<Position[]>>('/positions/history')
    return response.data.data || []
  }

  // ============================================
  // Account
  // ============================================

  async getBalance(): Promise<Balance> {
    const response = await this.client.get<ApiResponse<Balance>>('/account/balance')
    if (response.data.data) {
      return response.data.data
    }
    throw new Error('Failed to get balance')
  }

  async getAccountInfo(): Promise<any> {
    const response = await this.client.get<ApiResponse<any>>('/account/info')
    return response.data.data
  }

  // ============================================
  // Market Data
  // ============================================

  async getInstruments(): Promise<Instrument[]> {
    const response = await this.client.get<ApiResponse<Instrument[]>>('/market/instruments')
    return response.data.data || []
  }

  async getPrice(instrument: string): Promise<Price> {
    const response = await this.client.get<ApiResponse<Price>>(`/market/price/${instrument}`)
    if (response.data.data) {
      return response.data.data
    }
    throw new Error('Failed to get price')
  }

  async getHistoricalData(
    instrument: string,
    timeframe: Timeframe,
    limit: number = 100
  ): Promise<Candle[]> {
    const response = await this.client.get<ApiResponse<Candle[]>>(
      `/market/history/${instrument}`,
      {
        params: { timeframe, limit },
      }
    )
    return response.data.data || []
  }

  // ============================================
  // Chat
  // ============================================

  async sendMessage(message: string, modelType: string = 'QUICK'): Promise<ChatMessage> {
    if (import.meta.env.DEV) {
      console.log('Sending chat message:', { message: message.substring(0, 100) + '...', modelType })
    }
    
    try {
      // Use longer timeout for chat messages to prevent OpenRouter timeouts
      // NOTE: Do NOT add custom headers here - backend CORS only allows: Content-Type, Authorization, X-Session-ID
      const response = await this.client.post<ApiResponse<ChatMessage>>('/chat/message', {
        message,
        modelType,
      }, {
        timeout: 60000, // 60 seconds for LLM responses
      })
      
      if (response.data.data) {
        if (import.meta.env.DEV) {
          console.log('Chat response received:', { 
            id: response.data.data.id, 
            contentLength: response.data.data.content.length,
            model: response.data.data.model 
          })
        }
        return response.data.data
      }
      throw new Error('Failed to send message')
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('Send message error details:', {
          message: error.message,
          code: error.code,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        })
      }
      throw error
    }
  }

  async getChatHistory(limit: number = 100): Promise<ChatMessage[] | {
    messages: ChatMessage[]
    pagination: {
      total: number
      hasMore: boolean
      cursor: string | null
    }
  }> {
    const params: any = { limit }
    
    const response = await this.client.get<ApiResponse<any>>('/chat/history', { params })
    
    // Handle both old format (array) and new format (object with pagination)
    if (Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return response.data.data || { messages: [], pagination: { total: 0, hasMore: false, cursor: null } }
  }

  // ============================================
  // Approvals
  // ============================================

  async createApprovalRequest(tradeIntent: any): Promise<ApprovalRequest> {
    const response = await this.client.post<ApiResponse<ApprovalRequest>>('/approvals', {
      tradeIntent
    })
    if (response.data.data) {
      return response.data.data
    }
    throw new Error('Failed to create approval request')
  }

  async getPendingApprovals(): Promise<ApprovalRequest[]> {
    const response = await this.client.get<ApiResponse<ApprovalRequest[]>>('/approvals/pending')
    return response.data.data || []
  }

  async respondToApproval(approvalId: string, approved: boolean): Promise<OrderResult> {
    const response = await this.client.post<ApiResponse<OrderResult>>(
      `/approvals/${approvalId}/respond`,
      { approved }
    )
    if (response.data.data) {
      return response.data.data
    }
    throw new Error('Failed to respond to approval')
  }

  async getApprovalHistory(limit: number = 50): Promise<ApprovalRequest[]> {
    const response = await this.client.get<ApiResponse<ApprovalRequest[]>>('/approvals/history', {
      params: { limit },
    })
    return response.data.data || []
  }

  // ============================================
  // Watchlist
  // ============================================

  async getWatchlist(): Promise<WatchlistItem[]> {
    const response = await this.client.get<ApiResponse<WatchlistItem[]>>('/watchlist')
    return response.data.data || []
  }

  async addToWatchlist(instrument: string, notes?: string): Promise<void> {
    await this.client.post('/watchlist', { instrument, notes })
  }

  async removeFromWatchlist(instrument: string): Promise<void> {
    await this.client.delete(`/watchlist/${instrument}`)
  }

  async updateWatchlistNotes(instrument: string, notes: string): Promise<void> {
    await this.client.put(`/watchlist/${instrument}`, { notes })
  }

  // ============================================
  // Intelligence
  // ============================================

  async getOpportunities(): Promise<any[]> {
    const response = await this.client.get<ApiResponse<any[]>>('/intelligence/opportunities')
    return response.data.data || []
  }

  async getMarketContext(instrument: string): Promise<any> {
    const response = await this.client.get<ApiResponse<any>>(`/intelligence/market-context/${instrument}`)
    return response.data.data || null
  }

  async getNews(instruments?: string[]): Promise<any[]> {
    const params = instruments ? { instruments: instruments.join(',') } : undefined
    const response = await this.client.get<ApiResponse<any[]>>('/intelligence/news', { params })
    return response.data.data || []
  }

  // WhatsApp
  async getWhatsAppStatus(): Promise<any> {
    const response = await this.client.get('/whatsapp/status')
    return response.data.data
  }

  async getWhatsAppChats(): Promise<any[]> {
    const response = await this.client.get('/whatsapp/chats')
    return response.data.data || []
  }

  async sendWhatsAppMessage(to: string, message: string): Promise<any> {
    const response = await this.client.post('/whatsapp/send', { to, message })
    return response.data
  }

  async getAIMemoryLogs(): Promise<any[]> {
    const response = await this.client.get('/whatsapp/memory')
    return response.data.data || []
  }
}

// Export singleton instance
export const apiClient = new ApiClient()

// Load session on initialization
apiClient.loadSession()
